import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { PlusIcon, ArrowDownOnSquareIcon, XMarkIcon } from '@heroicons/react/24/outline';

interface VaultItemActionModalProps {
    isOpen: boolean;
    onClose: () => void;
    onCreateNew: () => void;
    onAddExisting: () => void;
    vaultName: string;
}

const VaultItemActionModal: React.FC<VaultItemActionModalProps> = ({
    isOpen,
    onClose,
    onCreateNew,
    onAddExisting,
    vaultName
}) => {
    useEffect(() => {
        const handleEscape = (e: KeyboardEvent) => {
            if (e.key === 'Escape') {
                onClose();
            }
        };

        if (isOpen) {
            document.addEventListener('keydown', handleEscape);
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscape);
            document.body.style.overflow = 'auto';
        };
    }, [isOpen, onClose]);

    if (!isOpen) return null;

    const handleBackdropClick = (e: React.MouseEvent) => {
        if (e.target === e.currentTarget) {
            onClose();
        }
    };

    const modalContent = (
        <div className="fixed inset-0 z-[1000] overflow-y-auto">
            {/* Enhanced Backdrop - matching Modal.tsx */}
            <div 
                className="fixed inset-0 bg-gradient-to-br from-black/60 to-black/40 backdrop-blur-sm transition-all duration-300"
                onClick={handleBackdropClick}
            />
            
            {/* Modal Container - matching Modal.tsx design */}
            <div className="flex min-h-full items-center justify-center p-2 sm:p-4">
                <div className="relative bg-white rounded-2xl sm:rounded-3xl w-full max-w-md mx-2 sm:mx-4 max-h-[90vh] sm:max-h-[95vh] overflow-hidden shadow-2xl border border-gray-100 transform transition-all">
                    {/* Enhanced Header - using project's primary colors */}
                    <div className="sticky top-0 z-10 bg-gradient-to-r from-primary-600 to-primary-700 text-white px-4 sm:px-6 py-4 sm:py-5">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center space-x-2 sm:space-x-3">
                                <div className="p-2 sm:p-2.5 bg-white/20 rounded-xl backdrop-blur-sm">
                                    <PlusIcon className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                                </div>
                                <div>
                                    <h3 className="text-lg sm:text-xl font-bold">
                                        Add to Vault
                                    </h3>
                                    <p className="text-primary-100 text-xs sm:text-sm">
                                        {vaultName}
                                    </p>
                                </div>
                            </div>
                            <button
                                onClick={onClose}
                                className="p-2 sm:p-3 text-white/80 hover:text-white hover:bg-white/20 rounded-xl transition-all duration-200"
                                aria-label="Close modal"
                            >
                                <XMarkIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                            </button>
                        </div>
                    </div>

                    {/* Content - matching Modal.tsx styling */}
                    <div className="bg-gray-50 max-h-[calc(90vh-120px)] sm:max-h-[calc(95vh-140px)] overflow-y-auto">
                        <div className="p-4 sm:p-6 space-y-4">
                            <button
                                onClick={() => {
                                    onCreateNew();
                                    onClose();
                                }}
                                className="w-full group relative bg-white hover:bg-gradient-to-r hover:from-primary-50 hover:to-primary-50 border-2 border-gray-200 hover:border-primary-300 rounded-xl sm:rounded-2xl p-4 sm:p-5 transition-all duration-300 shadow-sm hover:shadow-lg transform hover:-translate-y-1 active:translate-y-0"
                            >
                                <div className="flex items-center space-x-3 sm:space-x-4">
                                    <div className="relative">
                                        <div className="h-10 w-10 sm:h-12 sm:w-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-white group-hover:scale-110 transition-all duration-300 shadow-lg">
                                            <PlusIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                                        </div>
                                        <div className="absolute -top-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 bg-gradient-to-br from-green-400 to-green-500 rounded-full flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full"></div>
                                        </div>
                                    </div>
                                    <div className="flex-1 text-left">
                                        <h3 className="font-bold text-gray-900 text-base sm:text-lg group-hover:text-primary-700 transition-colors duration-200">
                                            Create New Item
                                        </h3>
                                        <p className="text-gray-600 text-sm mt-1 group-hover:text-gray-700 transition-colors duration-200">
                                            Add a new password, card, or secure note
                                        </p>
                                    </div>
                                    <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-0 translate-x-2">
                                        <div className="p-2 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 shadow-lg">
                                            <PlusIcon className="h-4 w-4 text-white" />
                                        </div>
                                    </div>
                                </div>
                            </button>

                            <button
                                onClick={() => {
                                    onAddExisting();
                                    onClose();
                                }}
                                className="w-full group relative bg-white hover:bg-gradient-to-r hover:from-purple-50 hover:to-purple-50 border-2 border-gray-200 hover:border-purple-300 rounded-xl sm:rounded-2xl p-4 sm:p-5 transition-all duration-300 shadow-sm hover:shadow-lg transform hover:-translate-y-1 active:translate-y-0"
                            >
                                <div className="flex items-center space-x-3 sm:space-x-4">
                                    <div className="relative">
                                        <div className="h-10 w-10 sm:h-12 sm:w-12 bg-gradient-to-br from-purple-500 to-purple-600 rounded-xl sm:rounded-2xl flex items-center justify-center text-white group-hover:scale-110 transition-all duration-300 shadow-lg">
                                            <ArrowDownOnSquareIcon className="h-5 w-5 sm:h-6 sm:w-6" />
                                        </div>
                                        <div className="absolute -top-1 -right-1 w-3 h-3 sm:w-4 sm:h-4 bg-gradient-to-br from-orange-400 to-orange-500 rounded-full flex items-center justify-center shadow-sm opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                                            <div className="w-1.5 h-1.5 sm:w-2 sm:h-2 bg-white rounded-full"></div>
                                        </div>
                                    </div>
                                    <div className="flex-1 text-left">
                                        <h3 className="font-bold text-gray-900 text-base sm:text-lg group-hover:text-purple-700 transition-colors duration-200">
                                            Add Existing Item
                                        </h3>
                                        <p className="text-gray-600 text-sm mt-1 group-hover:text-gray-700 transition-colors duration-200">
                                            Move or copy items from other vaults
                                        </p>
                                    </div>
                                    <div className="opacity-0 group-hover:opacity-100 transition-all duration-300 transform group-hover:translate-x-0 translate-x-2">
                                        <div className="p-2 rounded-lg bg-gradient-to-br from-purple-500 to-purple-600 shadow-lg">
                                            <ArrowDownOnSquareIcon className="h-4 w-4 text-white" />
                                        </div>
                                    </div>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );

    // Use portal to render modal at document.body level
    return createPortal(modalContent, document.body);
};

export default VaultItemActionModal; 